import { Work_Type } from "../../../../generated/graphql";
import { WorkingTemplatePolicyBinding } from "../../../../api/working-template-policy-api";

export type WorkingTemplateSelectedSubTab =
  | "WORKING_TIME"
  | "RECORDING_TIME"
  | "CONTROL_ALARM"
  | "LEAVE_WORK_MANAGEMENT"
  | "ETC_OPERATING_TIME"
  | "WIDGET_SETTING";

export type WorkingTemplateSaveStage =
  "idle" | "basic-saving" | "binding-saving" | "partial-failure" | "success";

export interface SelectedBasePolicyDetail {
  workPolicyId: number;
  policyKind: string;
  status: string;
}

export interface WorkingTemplatePolicySaveInput {
  workType: Work_Type | number;
  selectedSubTab: WorkingTemplateSelectedSubTab;
  workingTemplateIdx?: number;
  selectedPolicyId: number | null;
  selectedPolicyDetail: SelectedBasePolicyDetail | null;
  detailStatus: string;
  basicFingerprint: string;
  savedBasicFingerprint: string | null;
  basicAlreadySaved: boolean;
  createBasic: () => Promise<number>;
  updateBasic: (workingTemplateIdx: number) => Promise<void>;
  putBinding: (
    workingTemplateIdx: number,
    workPolicyId: number | null
  ) => Promise<WorkingTemplatePolicyBinding>;
  onStage: (stage: WorkingTemplateSaveStage) => void;
  onBasicSaved: (workingTemplateIdx: number, fingerprint: string) => void;
  onBindingSaved: (binding: WorkingTemplatePolicyBinding) => void;
  onBindingFailed: (error: unknown) => void;
}

export type WorkingTemplatePolicySaveResult =
  | {
      status: "success";
      workingTemplateIdx: number;
      binding: WorkingTemplatePolicyBinding;
    }
  | {
      status: "partial-failure";
      workingTemplateIdx: number;
      error: unknown;
    };

export class WorkingTemplatePolicySaveValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorkingTemplatePolicySaveValidationError";
  }
}

export function shouldPersistBasePolicyBinding(
  workType: Work_Type | number,
  selectedSubTab: WorkingTemplateSelectedSubTab
): boolean {
  return (
    (workType === Work_Type.WorkTypeManagementPolicy || workType === 10) &&
    selectedSubTab === "WORKING_TIME"
  );
}

function assertSelectedPolicyCanBeSaved(
  selectedPolicyId: number | null,
  detailStatus: string,
  detail: SelectedBasePolicyDetail | null
) {
  if (selectedPolicyId === null) return;
  if (
    detailStatus !== "success" ||
    !detail ||
    detail.workPolicyId !== selectedPolicyId ||
    detail.policyKind !== "BASE" ||
    detail.status !== "ACTIVE"
  ) {
    throw new WorkingTemplatePolicySaveValidationError(
      "선택한 기본 정책의 상세 정보를 확인한 뒤 저장해 주세요."
    );
  }
}

export async function saveWorkingTemplatePolicyBinding(
  input: WorkingTemplatePolicySaveInput
): Promise<WorkingTemplatePolicySaveResult> {
  if (!shouldPersistBasePolicyBinding(input.workType, input.selectedSubTab)) {
    throw new WorkingTemplatePolicySaveValidationError(
      "기본 정책 연결 저장 조건이 아닙니다."
    );
  }

  assertSelectedPolicyCanBeSaved(
    input.selectedPolicyId,
    input.detailStatus,
    input.selectedPolicyDetail
  );

  let workingTemplateIdx = input.workingTemplateIdx;
  const basicIsCurrent =
    Boolean(workingTemplateIdx) &&
    input.basicAlreadySaved &&
    input.savedBasicFingerprint === input.basicFingerprint;

  if (!basicIsCurrent) {
    input.onStage("basic-saving");
    if (workingTemplateIdx) {
      await input.updateBasic(workingTemplateIdx);
    } else {
      workingTemplateIdx = await input.createBasic();
    }
    input.onBasicSaved(workingTemplateIdx, input.basicFingerprint);
  }

  input.onStage("binding-saving");
  try {
    const binding = await input.putBinding(
      workingTemplateIdx as number,
      input.selectedPolicyId
    );
    input.onBindingSaved(binding);
    input.onStage("success");
    return {
      status: "success",
      workingTemplateIdx: workingTemplateIdx as number,
      binding
    };
  } catch (error) {
    input.onBindingFailed(error);
    input.onStage("partial-failure");
    return {
      status: "partial-failure",
      workingTemplateIdx: workingTemplateIdx as number,
      error
    };
  }
}
